/**
 * router/routes/compras.routes.ts
 * Compras (REQUI) module routes, nested under the tenant AppShell.
 * Split out of router/index.ts (openspec/arquitectura/convencion-modulo.md §4).
 */
import type { RouteRecordRaw } from 'vue-router';
import { depositoRouteName } from '../../modules/compras/deposito/deposito-base.js';
import { depositoRoutes } from './deposito.routes.js';

export const comprasRoutes: RouteRecordRaw[] = [
  // Compras (REQUI) — item-level purchase_case engine (Sesión 4,
  // openspec/requi/SDD-requi.md §12 paso 5). Literal siblings ('nueva',
  // 'aprobaciones') MUST come before the ':id' route so they aren't
  // captured as an id param (same ordering rule as rental/new above).
  //
  // add-compras-dashboard: /compras is now the Tablero de Compras
  // landing view (dashboard), same "bare module path -> dashboard"
  // convention as /servicios (ServiciosHomeView) and /rrhh (RrhhHomeView)
  // — see modules/dashboard/registry.ts, whose 'compras' entry already
  // pointed at '/compras' and now correctly opens the new dashboard
  // instead of the pipeline. The pipeline list moved to /compras/pipeline;
  // its route NAME is unchanged ('compras-pipeline'), so the one internal
  // link that referenced it by name (ComprasCaseDetailView.vue's back
  // button) needed no changes.
  {
    path: 'compras',
    name: 'compras-home',
    component: () => import('../../views/compras/ComprasHomeView.vue'),
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },

  {
    path: 'compras/pipeline',
    name: 'compras-pipeline',
    component: () => import('../../views/compras/ComprasPipelineView.vue'),
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },

  {
    path: 'compras/nueva',
    name: 'compras-new',
    component: () => import('../../views/compras/ComprasCaseCreateView.vue'),
    meta: { requiresCapability: 'requi.solicitud.crear' },
  },

  // 2026-08-31-requi-aprobacion-oc-por-monto (design.md D7) — widened to
  // any-of: a tier-1-only requi.comprador (requi.oc.aprobar) or a
  // tier-2-only requi.aprobador (requi.oc.aprobar-monto) must reach this
  // inbox too, even though neither holds the pre-existing
  // requi.caso.aprobar (SA-level approval, unrelated to PO approval).
  {
    path: 'compras/aprobaciones',
    name: 'compras-approvals',
    component: () => import('../../views/compras/ComprasApprovalInboxView.vue'),
    meta: { requiresCapability: ['requi.caso.aprobar', 'requi.oc.aprobar', 'requi.oc.aprobar-monto'] },
  },
  // What the approver approves for ONE pending purchase order (lines, prices, why this supplier). Owner = the SA
  // ('case') or the consolidation round ('round'); tier = 'n1' (Compras) / 'n2' (Gerencia).
  {
    path: 'compras/aprobaciones/oc/:owner(case|round)/:ownerId/:supplierId/:tier(n1|n2)',
    name: 'compras-po-approval-detail',
    component: () => import('../../views/compras/ComprasPoApprovalDetailView.vue'),
    meta: { requiresCapability: ['requi.oc.aprobar', 'requi.oc.aprobar-monto'] },
  },

  // fix/rental-demo ÍTEM 1 — listado global de OCs (todos los estados,
  // a diferencia de las "abiertas" del dashboard). Definida ANTES de
  // 'compras/:id' (misma regla de ordering que el resto de las rutas
  // literal-vs-:id de este archivo) para que 'ordenes' no se capture
  // como :id.
  {
    path: 'compras/ordenes',
    name: 'compras-purchase-orders',
    component: () => import('../../views/compras/PurchaseOrdersListView.vue'),
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },

  // requi-cotizacion-consolidada-por-rubro Slice A (design.md D13) — the
  // "Consolidar compras" tray. Definida ANTES de 'compras/:id' (misma
  // regla literal-vs-:id que el resto de este archivo) para que
  // 'demanda' no se capture como :id. The endpoint stays
  // requi.solicitud.leer (compras-http-routes); the NAV entry uses the
  // narrower requi.caso.editar (design D13's own rationale) — see
  // manifest-registry.ts.
  {
    path: 'compras/demanda',
    name: 'compras-demand-by-rubro',
    component: () => import('../../views/compras/ComprasDemandaPorRubroView.vue'),
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },

  // requi-cotizacion-consolidada-por-rubro (Slice C, task 3.34, design.md
  // D13) — the consolidated round's own workspace. Definida ANTES de
  // 'compras/:id' (misma regla literal-vs-:id) para que 'consolidado' no
  // se capture como :id. The endpoint stays requi.solicitud.leer; write
  // actions inside gate on requi.caso.editar (same canWrite pattern as
  // ComprasPurchaseOrderDetailView.vue).
  {
    path: 'compras/consolidado/:roundId',
    name: 'compras-consolidation',
    component: () => import('../../views/compras/ComprasConsolidacionView.vue'),
    props: true,
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },

  // requi-cotizacion-consolidada-por-rubro (Slice B, task 2.15, design.md
  // D6) — PO-centric detail, loaded by orderId alone (works for a
  // case-owned or round-owned OC). The old case-scoped path below now
  // redirects here — definida ANTES de esa ruta para que el redirect
  // (que reusa este mismo name) resuelva sin ambigüedad.
  {
    path: 'compras/oc/:orderId',
    name: 'compras-oc-detail',
    component: () => import('../../views/compras/ComprasPurchaseOrderDetailView.vue'),
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },

  // Mockup view_oc.png (SDD-lite 2026-08-23) — pantalla de detalle de OC,
  // distinta de la ficha print (compras-oc-print). Bookmarked/old links
  // to this case-scoped path redirect to the PO-centric route above
  // (design.md D12) — caseId is no longer needed to load an OC.
  // Notifications for new cases linked here before 2026-10-14 (no route
  // matched, the bell landed on the dashboard). Keeps those rows working.
  {
    path: 'compras/cases/:id',
    redirect: (to) => ({ name: 'compras-detail', params: { id: to.params['id'] } }),
  },
  {
    path: 'compras/:caseId/oc/:orderId',
    redirect: (to) => ({ name: 'compras-oc-detail', params: { orderId: to.params['orderId'] } }),
  },

  // odd/tasks/compras-deposito.md S1 — ONE Depósito screen set, mounted here
  // and under /servicios/deposito (deposito.routes.ts: same components, same
  // per-tab capabilities, own route names). Defined BEFORE 'compras/:id'
  // (literal-vs-:id ordering, same as the rest of this file). The old
  // /compras/stock table is folded into the home; its item view moved under
  // /compras/deposito/stock.
  ...depositoRoutes('compras'),
  { path: 'compras/stock', redirect: { name: depositoRouteName('compras', 'inicio') } },
  {
    path: 'compras/stock/:warehouseId/:itemMasterId',
    redirect: (to) => ({
      name: depositoRouteName('compras', 'item'),
      params: { warehouseId: to.params['warehouseId'], itemMasterId: to.params['itemMasterId'] },
    }),
  },

  {
    path: 'compras/:id',
    name: 'compras-detail',
    component: () => import('../../views/compras/ComprasCaseDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'requi.solicitud.leer' },
  },
];
